import { useState } from "react";
import { ArrowRight, Check, Cpu } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import EditorialSection from "@/components/EditorialSection";
import PlatformArchitecture from "@/components/PlatformArchitecture";
import OperationalFlow from "@/components/OperationalFlow";
import ProductWordmark from "@/components/ProductWordmark";
import ProductEvidence from "@/components/ProductEvidence";
import SectionReveal from "@/components/SectionReveal";
import DeploymentTopology from "@/components/DeploymentTopology";
import LeadDialog from "@/components/LeadDialog";
import NistAlignment from "@/components/NistAlignment";
import TrustBoundaryDiagram from "@/components/TrustBoundaryDiagram";
import { PRIMARY_BRAND_LOCKUP } from "@/config/brand";

const outcomes = [
  ["01", "Secure", "Reduce exposure and govern trusted access."],
  ["02", "Accelerate", "Move supported workloads through the best available path."],
  ["03", "Understand", "Connect live activity to authoritative operational state."],
  ["04", "Orchestrate", "Manage policy, nodes and response from one command environment."],
];

const useCases = [
  ["01", "Critical infrastructure", "See and control distributed services where resilience and accountability matter."],
  ["02", "Government and sovereign", "Operate within defined trust, locality and governance boundaries."],
  ["03", "Distributed enterprise", "Bring cloud, branch, edge and endpoint operations into one coherent view."],
];

export default function Home() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);

  return (
    <div className="lf-home">
      <SEO
        title="LightningFlow — Network Intelligence at the Speed of Light"
        description="Secure, understand, orchestrate and accelerate distributed networks from one intelligent enterprise platform."
        canonicalPath="/"
        image="/platform/lightningflow-command-center-v9.png"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "LightningFlow",
          url: "https://lightningflow.ai",
          description: "Secure network intelligence and orchestration for distributed infrastructure.",
        }}
      />

      <section className="lf-hero-editorial" data-testid="home-hero">
        <div className="lf-container lf-hero-grid">
          <div>
            <p className="lf-hero-kicker">{PRIMARY_BRAND_LOCKUP}</p>
            <h1 className="lf-hero-title" data-testid="primary-brand-strapline">
              Network Intelligence at the <em>Speed of Light.</em>
            </h1>
            <p className="lf-hero-summary">
              Protect your network. See what matters. Bring your teams and systems together in one clear view.
            </p>
            <div className="lf-hero-actions">
              <Link to="/platform" className="lf-btn-primary" data-testid="home-cta-explore-platform">
                Explore the Platform <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <button type="button" className="lf-btn-secondary" onClick={() => setDemoOpen(true)} data-testid="home-cta-early-access">
                Request a Demo
              </button>
            </div>
          </div>
          <div className="lf-hero-evidence-stack">
            <figure className="lf-hero-evidence">
              <img src="/platform/lightningflow-command-center-v9-960.webp" alt="LightningFlow Command Centre showing live topology, node state and operational intelligence" width="960" height="454" decoding="async" fetchpriority="high" />
              <figcaption>Your network, in one view. Command Centre preview; interface may change.</figcaption>
            </figure>
            <ol className="lf-outcomes" aria-label="LightningFlow platform outcomes">
              {outcomes.map(([number, title, detail]) => (
                <li key={title}><span>{number}</span><strong>{title}</strong><small>{detail}</small></li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-b border-ink-100 bg-white py-6" aria-label="Security framework alignment">
        <div className="lf-container"><NistAlignment compact /></div>
      </section>

      <EditorialSection id="platform" number="01" label="One platform" title="Multiple technologies. One operational system." body="Connect your people, sites and services. Bring security, visibility and everyday operations together, so your team can spend less time joining the dots.">
        <PlatformArchitecture />
      </EditorialSection>

      <EditorialSection id="intelligence" number="02" label="Operational intelligence" title="Visibility becomes valuable when it leads to understanding." body="See what changed, understand why it matters and choose your next step. QuantumCubeDB brings the context together; Sheila helps your team make sense of it." dark>
        <ProductWordmark product="quantumcubedb" className="mx-auto mt-8 mb-10 block h-auto w-full max-w-md" />
        <OperationalFlow />
        <div className="mt-10 flex flex-wrap gap-3">
          <Link className="lf-btn border border-white bg-white text-ink-900 hover:bg-cyan-200" to="/quantumcubedb">Explore QuantumCubeDB</Link>
          <Link className="lf-btn border border-white/40 text-white hover:border-white" to="/sheila-ai">Meet Sheila AI</Link>
        </div>
      </EditorialSection>

      <EditorialSection id="performance" number="03" label="Performance + control" title="More performance. A clearer view." body="LightningFlow Core brings topology, service health, policy and supported FPGA acceleration into one command environment, with a resilient software path when hardware acceleration is unavailable or unsuitable.">
        <ProductEvidence src="/platform/lightningflow-command-center-v9.webp" alt="LightningFlow Command Centre showing network topology, node health, operational intelligence and Halo endpoints" caption="Topology, service health and network activity, together. Command Centre preview; interface may change." meta="Command Centre · topology and node intelligence" />
        <div className="mt-10 grid gap-8 border-t border-ink-200 pt-8 md:grid-cols-2">
          <div className="flex gap-4"><Cpu className="mt-1 h-5 w-5 shrink-0 text-electric" aria-hidden="true" /><div><h3 className="font-display text-2xl font-bold">Hardware acceleration</h3><p className="mt-2 text-ink-600">Supported workloads can use dedicated acceleration with measured health and performance.</p></div></div>
          <div className="flex gap-4"><Check className="mt-1 h-5 w-5 shrink-0 text-electric" aria-hidden="true" /><div><h3 className="font-display text-2xl font-bold">Built to keep moving</h3><p className="mt-2 text-ink-600">A controlled software path helps services remain resilient.</p></div></div>
        </div>
      </EditorialSection>

      <EditorialSection id="security" number="04" label="Security architecture" title="Clear access. Stronger protection." body="LightningFlow combines reduced exposure, identity- and policy-aware access, protected connections and explainable operational state across defined trust boundaries." dark>
        <TrustBoundaryDiagram dark />
        <Link className="lf-btn mt-9 border border-white bg-white text-ink-900 hover:bg-cyan-200" to="/security">Review Security &amp; Trust</Link>
      </EditorialSection>

      <EditorialSection id="deployment" number="05" label="Deployment" title="One operational model across every supported environment." body="Run closer to users and workloads without fragmenting visibility, policy or command.">
        <DeploymentTopology />
      </EditorialSection>

      <EditorialSection id="use-cases" number="06" label="Where it matters" title="Built for networks where clarity and control are not optional.">
        <div className="lf-editorial-list">
          {useCases.map(([number, title, body]) => <article key={title}><span className="lf-list-index">{number}</span><h3>{title}</h3><p>{body}</p></article>)}
        </div>
        <Link className="lf-btn-primary mt-10" to="/use-cases">Explore use cases <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
      </EditorialSection>


      <section className="border-b border-ink-100 bg-white py-16 md:py-20" data-testid="home-consumer-products" aria-label="LightningFlow products for people">
        <div className="lf-container">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="lf-eyebrow">For people</p>
              <h2 className="mt-4 max-w-2xl font-display text-4xl font-black tracking-tight md:text-5xl">Protection you can use every day.</h2>
              <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-600">The same LightningFlow security discipline, built for personal devices and AI workflows.</p>
            </div>
            <Link className="lf-btn-secondary shrink-0" to="/platform">For teams · Explore the Platform <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          </div>
          <SectionReveal className="lf-product-collection mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="home-consumer-grid">
            <Link to="/halo-fortress" className="group flex flex-col justify-between border border-[#0c2b45] bg-[#061122] p-7 text-white transition-colors hover:border-cyan-300 md:col-span-2 xl:col-span-1 xl:row-span-2" data-testid="home-product-halo-fortress">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[.2em] text-cyan-200">For your Mac</p>
                <img src="/halo/fortress-wordmark-6dc95dac.png" alt="Fortress" width="2172" height="724" loading="lazy" decoding="async" className="mt-5 h-auto w-full max-w-xs object-contain object-left" />
                <h3 className="sr-only">Fortress</h3>
                <p className="mt-5 text-base leading-relaxed text-slate-200">The Mac app with Halo SuperVPN and SecureShield together. A private connection and a clearer view of your Mac’s security, in one place.</p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 group-hover:text-white">Discover Fortress <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></span>
            </Link>
            {[
              ["/halo", "Halo SuperVPN", "Private connectivity with a clear protection state on every device."],
              ["/secureshield", "SecureShield", "See what’s actually protecting your Mac — then fix it."],
              ["/secureshield-pro", "SecureShield Pro", "SecureShield plus Network Guard — see what’s connecting."],
              ["/secureai", "SecureAI", "Protect the path your AI tools take — without reading prompts."],
            ].map(([to, title, body]) => (
              <Link key={to} to={to} className="border border-ink-100 bg-ink-50/40 p-6 transition-colors hover:border-electric hover:bg-white" data-testid={`home-product-${to.slice(1)}`}>
                <h3 className="font-display text-xl font-bold text-ink-900">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-600">{body}</p>
                <span className="lf-link mt-5">Learn more <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></span>
              </Link>
            ))}
          </SectionReveal>
        </div>
      </section>

      <section className="lf-editorial-section lf-halo-transition" data-testid="home-halo-feature">
        <div className="lf-container grid gap-10 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
          <div>
            <p className="lf-hero-kicker">07 · Fortress</p>
            <h2 className="lf-editorial-title" data-testid="home-fortress-feature-title">Fortress — one Mac app for your connection and your Mac.</h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-slate-200">Halo SuperVPN and SecureShield, together in one simple Mac app. On your iPhone and other devices, Halo SuperVPN uses the same subscription.</p>
            <div className="mt-8 flex flex-wrap gap-3"><Link className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:bg-white" to="/halo-fortress" data-testid="home-feature-fortress">Discover Fortress <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link><Link className="lf-btn border border-white/40 text-white hover:border-white" to="/halo">Discover Halo SuperVPN</Link><Link className="lf-btn border border-white/40 text-white hover:border-white" to="/account">LightningFlow Account</Link></div>
          </div>
          <img src="/halo/fortress-wordmark-6dc95dac.png" alt="" aria-hidden="true" width="2172" height="724" loading="lazy" decoding="async" className="hidden h-auto w-full max-w-md justify-self-end object-contain lg:block" />
        </div>
      </section>

      <section className="lf-editorial-section bg-white" id="contact">
        <div className="lf-container grid gap-10 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
          <div><p className="lf-eyebrow">Start a conversation</p><h2 className="lf-statement mt-6">See what your network can become.</h2></div>
          <div className="flex flex-col gap-3 lg:items-start"><button type="button" className="lf-btn-primary" onClick={() => setDemoOpen(true)}>Request a Demo <ArrowRight className="h-4 w-4" /></button><button type="button" className="lf-btn-secondary" onClick={() => setPartnerOpen(true)}>Partner With Us</button></div>
        </div>
      </section>

      <LeadDialog open={demoOpen} onOpenChange={setDemoOpen} kind="demo" title="See LightningFlow in action" description="Tell us about the network environment you want to understand, protect or accelerate." />
      <LeadDialog open={partnerOpen} onOpenChange={setPartnerOpen} kind="pilot" title="Partner with LightningFlow" description="Tell us where LightningFlow could fit in your infrastructure or customer environment." />
    </div>
  );
}
